<template>
  <b-modal
    v-model="show"
    :title="`Comentarios — ${titulo}`"
    size="lg"
    centered
    hide-footer
    body-class="p-0"
  >
    <div v-if="cargando" class="text-center text-muted my-3">
      <b-spinner small class="align-middle mr-50" /> Cargando…
    </div>

    <template v-else>
      <!-- Hilo tipo chat -->
      <div ref="hilo" class="pc-hilo">
        <div v-if="!comentarios.length" class="pc-vacio">
          <i class="fa-regular fa-comments"></i>
          <p>Todavía no hay comentarios.<br />Escribe el primero abajo.</p>
        </div>

        <div v-for="c in comentarios" :key="c.id" class="pc-msg" :class="{ 'pc-msg--mio': c.mio }">
          <div class="pc-avatar" :title="c.usuarioNombre || '—'">{{ iniciales(c.usuarioNombre) }}</div>
          <div class="pc-msg__cuerpo">
            <div class="pc-msg__meta">
              <span class="pc-msg__autor">{{ c.usuarioNombre || '—' }}</span>
              <span class="pc-msg__fecha">{{ formatFecha(c.createdAt) }}</span>
              <template v-if="c.mio && editandoId !== c.id">
                <i class="fa-solid fa-pen pc-msg__accion" title="Editar" @click="empezarEdicion(c)"></i>
                <i class="fa-solid fa-trash-can pc-msg__accion pc-msg__accion--rojo" title="Borrar" @click="borrar(c)"></i>
              </template>
            </div>
            <div v-if="editandoId === c.id" class="pc-editor">
              <b-form-input
                v-model="editandoTexto"
                size="sm"
                autofocus
                @keyup.enter="guardarEdicion(c)"
                @keyup.esc="editandoId = null"
              />
              <b-button size="sm" variant="primary" class="btn-icon" title="Guardar" :disabled="!editandoTexto.trim()" @click="guardarEdicion(c)">
                <i class="fa-solid fa-check"></i>
              </b-button>
              <b-button size="sm" variant="outline-secondary" class="btn-icon" title="Cancelar" @click="editandoId = null">
                <i class="fa-solid fa-xmark"></i>
              </b-button>
            </div>
            <div v-else class="pc-burbuja">{{ c.texto }}</div>
          </div>
        </div>
      </div>

      <!-- Composer -->
      <div class="pc-composer">
        <b-form-textarea
          v-model="nuevo"
          rows="2"
          max-rows="5"
          placeholder="Escribe un comentario… (Enter para enviar)"
          @keydown.enter.exact.prevent="comentar"
        />
        <b-button variant="primary" class="pc-composer__btn" :disabled="!nuevo.trim()" @click="comentar">
          <i class="fa-solid fa-paper-plane mr-25"></i> Enviar
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BSpinner, BButton, BFormInput, BFormTextarea,
} from 'bootstrap-vue'
import axios from '@axios'

export default {
  components: {
    BModal, BSpinner, BButton, BFormInput, BFormTextarea,
  },
  data() {
    return {
      show: false,
      pedido: null,
      base: '/escaner/pedidos',
      titulo: '',
      comentarios: [],
      cargando: false,
      nuevo: '',
      editandoId: null,
      editandoTexto: '',
    }
  },
  methods: {
    // opts.base permite reutilizar el modal con otras entidades (p. ej. '/customers')
    abrir(entidad, opts = {}) {
      this.pedido = entidad
      this.base = opts.base || '/escaner/pedidos'
      this.titulo = opts.titulo || entidad.ref || ''
      this.comentarios = []
      this.nuevo = ''
      this.editandoId = null
      this.show = true
      this.cargar()
    },
    cargar() {
      this.cargando = true
      axios
        .get(`${this.base}/${this.pedido.id}/comentarios`)
        .then(({ data }) => {
          this.comentarios = data || []
          this.$nextTick(() => {
            const hilo = this.$refs.hilo
            if (hilo) hilo.scrollTop = hilo.scrollHeight
          })
        })
        .catch(() => { this.notificar('No se pudieron cargar los comentarios', 'danger') })
        .finally(() => { this.cargando = false })
    },
    iniciales(nombre) {
      return (nombre || '?')
        .split(/[\s@]+/)
        .slice(0, 2)
        .map(p => (p[0] || '').toUpperCase())
        .join('')
    },
    comentar() {
      const texto = this.nuevo.trim()
      if (!texto) return
      axios
        .post(`${this.base}/${this.pedido.id}/comentarios`, { texto })
        .then(() => {
          this.nuevo = ''
          this.cargar()
        })
        .catch(() => { this.notificar('No se pudo añadir el comentario', 'danger') })
    },
    empezarEdicion(c) {
      this.editandoId = c.id
      this.editandoTexto = c.texto
    },
    guardarEdicion(c) {
      const texto = this.editandoTexto.trim()
      if (!texto) return
      axios
        .put(`${this.base}/comentarios/${c.id}`, { texto })
        .then(() => {
          this.editandoId = null
          this.cargar()
        })
        .catch(e => {
          const msg = (e.response && e.response.data && e.response.data.msg) || 'No se pudo editar el comentario'
          this.notificar(msg, 'danger')
        })
    },
    borrar(c) {
      axios
        .delete(`${this.base}/comentarios/${c.id}`)
        .then(() => { this.cargar() })
        .catch(e => {
          const msg = (e.response && e.response.data && e.response.data.msg) || 'No se pudo borrar el comentario'
          this.notificar(msg, 'danger')
        })
    },
    formatFecha(f) {
      if (!f) return '--'
      const d = new Date(f)
      return Number.isNaN(d.getTime()) ? f : d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })
    },
    notificar(msg, variant = 'success') {
      this.$bvToast.toast(msg, { title: 'Comentarios', variant, solidColor: true })
    },
  },
}
</script>

<style lang="scss" scoped>
$primary: #7367f0;

// Hilo tipo chat
.pc-hilo {
  max-height: 55vh;
  min-height: 260px;
  overflow-y: auto;
  padding: 16px 20px;
  background: #f8f8fc;
}

.pc-vacio {
  text-align: center;
  color: #b9b9c3;
  padding: 48px 0;

  i { font-size: 34px; }
  p { margin: 10px 0 0; font-size: 13px; }
}

.pc-msg {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;

  &__cuerpo { max-width: 75%; }

  &__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 3px;
  }

  &__autor {
    font-size: 11px;
    font-weight: 700;
    color: #5e5873;
  }

  &__fecha {
    font-size: 10px;
    color: #b9b9c3;
  }

  &__accion {
    font-size: 10px;
    color: #b9b9c3;
    cursor: pointer;

    &:hover { color: $primary; }
    &--rojo:hover { color: #ea5455; }
  }

  // Mis mensajes: alineados a la derecha, burbuja en color primario
  &--mio {
    flex-direction: row-reverse;

    .pc-msg__meta { justify-content: flex-end; }

    .pc-burbuja {
      background: $primary;
      color: #fff;
      border-radius: 12px 2px 12px 12px;
    }
  }
}

.pc-avatar {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba($primary, 0.12);
  color: $primary;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pc-burbuja {
  display: inline-block;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 2px 12px 12px 12px;
  padding: 8px 12px;
  font-size: 13px;
  white-space: pre-line;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.pc-msg--mio .pc-burbuja { border: none; }

.pc-editor {
  display: flex;
  gap: 6px;
  min-width: 320px;
}

// Composer fijo abajo
.pc-composer {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid #ebe9f1;
  background: #fff;

  textarea { resize: none; }

  &__btn { white-space: nowrap; }
}
</style>
